import React, { useState, useEffect } from 'react';
import { API_ENDPOINTS } from '../config/constants';
import './TriggersConfigTab.css';

export default function TriggersConfigTab({ listId, projectId, onSave, enabled = false }) {
  const [triggers, setTriggers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingTriggerId, setEditingTriggerId] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [error, setError] = useState(null);

  // Formulário para novo/editar trigger
  const [formData, setFormData] = useState({
    name: '',
    keywords: [],
    caseSensitive: false,
    enabled: true
  });
  const [newKeyword, setNewKeyword] = useState('');

  // Carregar triggers quando a ferramenta for habilitada
  useEffect(() => {
    if (enabled && listId) {
      loadTriggers();
    } else if (!enabled) {
      // Limpar dados quando desabilitar
      setTriggers([]);
      setError(null);
    }
  }, [enabled, listId]);

  const loadTriggers = async () => {
    try {
      setError(null);
      
      if (!listId) {
        console.warn('[TriggersConfigTab] listId não fornecido');
        setTriggers([]);
        return;
      }
      
      setLoading(true);
      const url = `/agents/tools/triggers/list?list_id=${listId}`;
      console.log('[TriggersConfigTab] Carregando gatilhos de:', url);
      
      const response = await fetch(url);
      
      if (!response.ok) {
        const errorData = await response.json();
        console.error('[TriggersConfigTab] Erro na resposta:', errorData);
        setError(errorData.error || 'Erro ao carregar gatilhos');
        setTriggers([]);
        return;
      }
      
      const data = await response.json();
      console.log('[TriggersConfigTab] Resposta recebida:', data);
      
      if (data.ok) {
        setTriggers(data.triggers || []);
        setError(null);
      } else {
        console.error('[TriggersConfigTab] Erro ao carregar gatilhos:', data.error);
        setError(data.error || 'Erro desconhecido');
        setTriggers([]);
      }
    } catch (error) {
      console.error('[TriggersConfigTab] Erro ao carregar gatilhos:', error);
      setError(error.message || 'Erro ao carregar gatilhos');
      setTriggers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddKeyword = () => {
    if (!newKeyword.trim()) return;
    if (formData.keywords.includes(newKeyword.trim())) {
      alert('Palavra-chave já adicionada');
      return;
    }
    setFormData({
      ...formData,
      keywords: [...formData.keywords, newKeyword.trim()]
    });
    setNewKeyword('');
  };

  const handleRemoveKeyword = (keyword) => {
    setFormData({
      ...formData,
      keywords: formData.keywords.filter(k => k !== keyword)
    });
  };

  const handleSaveTrigger = async () => {
    try {
      if (!formData.name.trim()) {
        alert('Nome do gatilho é obrigatório');
        return;
      }
      if (formData.keywords.length === 0) {
        alert('Adicione pelo menos uma palavra-chave');
        return;
      }

      setLoading(true);

      const response = await fetch(
        `/agents/tools/triggers/save`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            list_id: listId,
            project_id: projectId,
            trigger: formData
          })
        }
      );

      const data = await response.json();

      if (!response.ok) {
        alert('Erro: ' + (data.error || 'Falha ao salvar gatilho'));
        return;
      }

      // Recarregar gatilhos
      await loadTriggers();

      // Limpar formulário
      setFormData({
        name: '',
        keywords: [],
        caseSensitive: false,
        enabled: true
      });
      setShowAddForm(false);
      setEditingTriggerId(null);

      if (onSave) onSave();
    } catch (error) {
      console.error('Erro ao salvar gatilho:', error);
      alert('Erro ao salvar gatilho');
    } finally {
      setLoading(false);
    }
  };

  const handleEditTrigger = (trigger) => {
    setFormData(trigger);
    setEditingTriggerId(trigger.id);
    setShowAddForm(true);
  };

  const handleDeleteTrigger = async (triggerId) => {
    if (!window.confirm('Tem certeza que deseja deletar este gatilho?')) return;

    try {
      setLoading(true);
      const response = await fetch(
        `/agents/tools/triggers/${triggerId}?list_id=${listId}`,
        { method: 'DELETE' }
      );

      const data = await response.json();

      if (!response.ok) {
        alert('Erro: ' + (data.error || 'Falha ao deletar gatilho'));
        return;
      }

      await loadTriggers();
      if (onSave) onSave();
    } catch (error) {
      console.error('Erro ao deletar gatilho:', error);
      alert('Erro ao deletar gatilho');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setShowAddForm(false);
    setEditingTriggerId(null);
    setFormData({
      name: '',
      keywords: [],
      caseSensitive: false,
      enabled: true
    });
    setNewKeyword('');
  };

  return (
    <div className="triggers-config-tab">
      <div className="triggers-header">
        <h3>⚡ Gatilhos (Triggers)</h3>
        <p className="triggers-description">
          Configure palavras-chave que, quando aparecerem nas mensagens, movem automaticamente o card para esta lista.
        </p>
      </div>

      {error && (
        <div style={{
          background: '#fee2e2',
          color: '#991b1b',
          padding: '12px 16px',
          borderRadius: '6px',
          marginBottom: '16px',
          fontSize: '14px',
          border: '1px solid #fecaca'
        }}>
          ⚠️ {error}
        </div>
      )}

      {!showAddForm ? (
        <button
          className="btn-add-trigger"
          onClick={() => setShowAddForm(true)}
          disabled={loading}
        >
          + Adicionar Gatilho
        </button>
      ) : null}

      {/* Formulário de Adicionar/Editar */}
      {showAddForm && (
        <div className="trigger-form">
          <h4>{editingTriggerId ? 'Editar Gatilho' : 'Novo Gatilho'}</h4>

          <div className="form-group">
            <label>Nome do Gatilho *</label>
            <input
              type="text"
              placeholder="Ex: Palavras de Urgência"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label>Palavras-chave *</label>
            <div className="keywords-input">
              <input
                type="text"
                placeholder="Digite uma palavra-chave e pressione Enter ou clique em Adicionar"
                value={newKeyword}
                onChange={(e) => setNewKeyword(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddKeyword();
                  }
                }}
                disabled={loading}
              />
              <button
                type="button"
                onClick={handleAddKeyword}
                disabled={loading || !newKeyword.trim()}
                className="btn-add-keyword"
              >
                Adicionar
              </button>
            </div>

            {formData.keywords.length > 0 && (
              <div className="keywords-list">
                {formData.keywords.map((keyword) => (
                  <div key={keyword} className="keyword-tag">
                    <span>{keyword}</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveKeyword(keyword)}
                      disabled={loading}
                      className="btn-remove-keyword"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="form-group checkbox">
            <label>
              <input
                type="checkbox"
                checked={formData.caseSensitive}
                onChange={(e) =>
                  setFormData({ ...formData, caseSensitive: e.target.checked })
                }
                disabled={loading}
              />
              Diferenciar maiúsculas/minúsculas
            </label>
          </div>

          <div className="form-group checkbox">
            <label>
              <input
                type="checkbox"
                checked={formData.enabled}
                onChange={(e) =>
                  setFormData({ ...formData, enabled: e.target.checked })
                }
                disabled={loading}
              />
              Gatilho ativado
            </label>
          </div>

          <div className="form-actions">
            <button
              className="btn-save"
              onClick={handleSaveTrigger}
              disabled={loading}
            >
              {loading ? 'Salvando...' : 'Salvar Gatilho'}
            </button>
            <button
              className="btn-cancel"
              onClick={handleCancel}
              disabled={loading}
            >
              Cancelar
            </button>
          </div>
        </div>
      )}

      {/* Lista de Gatilhos */}
      {triggers.length > 0 && (
        <div className="triggers-list">
          <h4>Gatilhos Configurados ({triggers.length})</h4>
          {triggers.map((trigger) => (
            <div key={trigger.id} className="trigger-item">
              <div className="trigger-info">
                <div className="trigger-name">
                  {trigger.enabled ? '✅' : '⭕'} {trigger.name}
                </div>
                <div className="trigger-keywords">
                  {trigger.keywords.map((keyword) => (
                    <span key={keyword} className="keyword-badge">
                      {keyword}
                    </span>
                  ))}
                </div>
                {trigger.caseSensitive && (
                  <div className="trigger-meta">Case-sensitive</div>
                )}
              </div>
              <div className="trigger-actions">
                <button
                  className="btn-edit"
                  onClick={() => handleEditTrigger(trigger)}
                  disabled={loading}
                  title="Editar"
                >
                  ✏️
                </button>
                <button
                  className="btn-delete"
                  onClick={() => handleDeleteTrigger(trigger.id)}
                  disabled={loading}
                  title="Deletar"
                >
                  🗑️
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {triggers.length === 0 && !showAddForm && (
        <div className="empty-state">
          <p>Nenhum gatilho configurado ainda.</p>
          <p>Adicione gatilhos para mover automaticamente cards quando palavras-chave aparecerem nas mensagens.</p>
        </div>
      )}
    </div>
  );
}
